/**
 * Tier 1: Shape API for Word Desktop (WordApiDesktop 1.2).
 *
 * Inserts floating TextBox shapes at the cursor position.
 * Each shape is colored per signer and can be freely dragged by the user.
 * Shape positions are read back in points and converted to page percentages
 * for the backend SignatureField model.
 */

import type { SignaturePlacement } from './useSignatureTags'
import { devLog } from '../services/devLog'

const SIG_SHAPE_PREFIX = 'kobsign:sig:'

/** Default shape dimensions in points (~30% × ~4.3% of A4) */
const SHAPE_WIDTH_PT = 180
const SHAPE_HEIGHT_PT = 36

/** Insert a floating signature TextBox at the current cursor position */
export async function insertSignatureShape(
  email: string,
  name: string,
  colorHex: string,
  colorHexLight: string,
): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection()
    const paragraph = selection.paragraphs.getFirst()
    paragraph.load('parentBody')
    await context.sync()

    // @ts-expect-error -- insertShape requires WordApiDesktop 1.2 (not in base types)
    const shape = paragraph.insertShape(
      'TextBox',
      SHAPE_WIDTH_PT,
      SHAPE_HEIGHT_PT,
    ) as Word.Shape

    // Tag with signer info for later retrieval
    shape.name = `${SIG_SHAPE_PREFIX}${email}|${name}`

    // Style: light fill with colored border
    // lineFormat is WordApiDesktop 1.2 — not in base @types/office-js
    try {
      shape.fill.setSolidColor(colorHexLight)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const s = shape as any
      if (s.lineFormat) {
        s.lineFormat.color = colorHex
        s.lineFormat.weight = 1.5
      }
    } catch {
      devLog.warn('Shape', 'Could not set fill/line style')
    }

    // Add label text inside the shape
    // textFrame.getRange() is WordApiDesktop 1.2
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tf = shape.textFrame as any
      const textRange = tf.getRange ? tf.getRange() : null
      if (textRange) {
        textRange.insertText(`\u270D ${name}`, Word.InsertLocation.replace)
        textRange.font.size = 9
        textRange.font.color = colorHex
        textRange.font.bold = true
      }
    } catch {
      devLog.warn('Shape', 'Could not set text content')
    }

    // Make it float with square text wrap
    try {
      shape.textWrap.type = Word.ShapeTextWrapType.square
    } catch {
      devLog.warn('Shape', 'Could not set text wrap')
    }

    await context.sync()
    devLog.info('Shape', `Inserted signature shape for ${email}`)
  })
}

/** Read all kobsign:sig: shapes and convert positions to page percentages */
export async function readSignatureShapes(): Promise<SignaturePlacement[]> {
  return await Word.run(async (context) => {
    const shapes = context.document.body.shapes
    shapes.load('items/name,items/top,items/left,items/width,items/height')
    await context.sync()

    // Get page dimensions from first section
    const section = context.document.sections.getFirst()
    section.load('pageSetup')
    await context.sync()

    const pageWidth = section.pageSetup.pageWidth
    const pageHeight = section.pageSetup.pageHeight
    const marginLeft = section.pageSetup.leftMargin
    const marginTop = section.pageSetup.topMargin

    const placements: SignaturePlacement[] = []

    for (const shape of shapes.items) {
      if (!shape.name?.startsWith(SIG_SHAPE_PREFIX)) continue

      const payload = shape.name.slice(SIG_SHAPE_PREFIX.length)
      const pipeIndex = payload.indexOf('|')
      const email = pipeIndex >= 0 ? payload.slice(0, pipeIndex) : payload
      const name = pipeIndex >= 0 ? payload.slice(pipeIndex + 1) : email

      // Shape coordinates are relative to page margin — add margin offset
      const absoluteX = shape.left + marginLeft
      const absoluteY = shape.top + marginTop

      placements.push({
        email,
        name,
        page: 1, // Shape API does not expose page number — v1 limitation
        x: (absoluteX / pageWidth) * 100,
        y: (absoluteY / pageHeight) * 100,
        width: (shape.width / pageWidth) * 100,
        height: (shape.height / pageHeight) * 100,
      })
    }

    devLog.info('Shapes', `Read ${placements.length} signature shape(s)`)
    return placements
  })
}

/** Remove a specific signer's signature shape(s) */
export async function removeSignatureShape(email: string): Promise<void> {
  await Word.run(async (context) => {
    const shapes = context.document.body.shapes
    shapes.load('items/name')
    await context.sync()

    const target = `${SIG_SHAPE_PREFIX}${email}|`
    for (const shape of shapes.items) {
      if (shape.name?.startsWith(target)) {
        shape.delete()
      }
    }
    await context.sync()
    devLog.info('Shapes', `Removed signature shape(s) for ${email}`)
  })
}

/** Remove all kobsign:sig: shapes from the document */
export async function removeSignatureShapes(): Promise<void> {
  await Word.run(async (context) => {
    const shapes = context.document.body.shapes
    shapes.load('items/name')
    await context.sync()

    let count = 0
    for (const shape of shapes.items) {
      if (shape.name?.startsWith(SIG_SHAPE_PREFIX)) {
        shape.delete()
        count++
      }
    }
    await context.sync()
    devLog.info('Shapes', `Removed ${count} signature shape(s)`)
  })
}
